import clsx from 'clsx'
import styles from './email-form-address-step.module.scss'
import { Button, InputRedesigned, Typography } from '@/ui-kit'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { SignInEmailType } from '@/schemes/sign-in-email-schema'
import { useTranslation } from 'react-i18next'
import { CloseIcon, SignInWarnIcon } from '@/assets/icons'
import { Dispatch, ReactNode, SetStateAction, useEffect } from 'react'
import i18n from '@/i18n'
import { ButtonTextColor } from '@/ui-kit/button'
import { DefaultEmailHeader } from '../default-email-header'
import { SignUpActions } from '../sign-up-actions'

interface Props {
    signInEmailMethods: UseFormReturn<SignInEmailType>
    isLoading: boolean
    error: string | null
    useGradient?: boolean
    nextBtnTitle?: string
    nextBtnStyles?: string
    disabledNextBtnStyles?: string
    showSkipBtn?: boolean
    skipBtnTitle?: string
    skipBtnStyles?: string
    skipBtnTextColor?: ButtonTextColor
    nextBtnTextColor?: ButtonTextColor
    className?: string
    emailHeader?: ReactNode
    titleKey?: string
    subtitleKey?: string
    setError: Dispatch<SetStateAction<string | null>>
    sendCode: () => void
    onSkip?: () => void
}

export const EmailFormAddressStep: React.FC<Props> = ({
    signInEmailMethods,
    isLoading,
    error,
    showSkipBtn = false,
    skipBtnTitle = i18n.t('registration.skip_and_finish_signup'),
    skipBtnStyles = styles.submitBtn,
    nextBtnTitle = i18n.t('auth.submit_btn'),
    nextBtnStyles = styles.submitBtn,
    disabledNextBtnStyles = styles.submitBtnDisabled,
    skipBtnTextColor = 'white',
    nextBtnTextColor = 'white',
    className,
    emailHeader,
    titleKey = 'auth.title',
    subtitleKey = 'auth.subtitle',
    setError,
    sendCode,
    onSkip,
}) => {
    const { t } = useTranslation()

    const {
        register: signInWithEmailRegister,
        handleSubmit: signInWithEmailSubmit,
        formState: { isValid, isDirty },
    } = signInEmailMethods

    const isConitnueBtnDisabled = !isValid || isLoading
    
    const showSignInViaTg = false

    useEffect(() => {
        // TODO: collect analytics
        signInEmailMethods.watch(async _ => {
            const res = await signInEmailMethods.trigger('email')
            if (res) setError(null)
            else setError(t('auth.invalid_email'))
        })
    }, [])

    const onSignInViaTg = () => {
        // TODO: handle click
    }

    const onClearClick = () => {
        signInEmailMethods.reset()
    }
    
    return (
        <FormProvider { ...signInEmailMethods }>
            <form
                className={
                    clsx(
                        styles.wrapper,
                        className
                    )
                }
                onSubmit={signInWithEmailSubmit(sendCode)}>
                {
                    emailHeader ??
                    <DefaultEmailHeader
                        titleKey={titleKey}
                        subtitleKey={subtitleKey} />
                }
                <div className={styles.input}>
                    <InputRedesigned
                        placeholder={t('auth.email_placeholder')}
                        required
                        className={
                            clsx(
                                (
                                    error ||
                                    (!isValid && isDirty)
                                ) &&
                                styles.inputError
                            )
                        }
                        {...signInWithEmailRegister('email', { required: true })} />
                    {
                        isDirty &&
                        <button type="button" className={styles.clearBtn} onClick={onClearClick}>
                            <CloseIcon className={styles.clearBtnIcon} />
                        </button>
                    }
                </div>
                {
                    error &&
                    <div className={styles.errors}>
                        <div className={styles.error}>
                            <SignInWarnIcon className={clsx(styles.errorIcon, styles.emailErrorIcon)} />
                            <Typography className={styles.errorLabel}>
                                {error}
                            </Typography>

                        </div>
                    </div>
                }
                <SignUpActions className={styles.actions}>            
                    {
                        showSignInViaTg &&
                        <Button
                            textColor="black"
                            className={styles.signInViaTgBtn}
                            isLoading={isLoading}
                            isDisabled={!isLoading}
                            onClick={onSignInViaTg}>{t('auth.sign_in_via_tg')}</Button>
                    }
                    <Button
                        type="submit"
                        variant="stretched"
                        typeColor="white"
                        textColor={nextBtnTextColor}
                        className={
                            clsx(
                                nextBtnStyles,
                                isConitnueBtnDisabled && disabledNextBtnStyles
                            )
                        }
                        isLoading={isLoading}
                        isDisabled={isConitnueBtnDisabled}>{nextBtnTitle}</Button>
                    {
                        showSkipBtn &&
                        <Button
                            type="button"
                            variant="stretched"
                            typeColor="white"
                            textColor={skipBtnTextColor}
                            className={skipBtnStyles}
                            isLoading={isLoading}
                            onClick={onSkip}>{skipBtnTitle}</Button>
                    }
                </SignUpActions>
            </form>
        </FormProvider>
    );
}
